import { MapPin, Phone, Mail, MessageCircle, Clock } from "lucide-react";
import { PageHero } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { PortalButton } from "@/components/PortalButton";
import { BRAND } from "@/config";

const phoneHref = `tel:${BRAND.phone.replace(/[^+\d]/g, "")}`;
const whatsappHref = `https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}`;
const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BRAND.address)}`;

const CONTACTS = [
  { key: "address", icon: MapPin, title: "Warehouse", value: BRAND.address, href: mapsHref },
  { key: "phone", icon: Phone, title: "Phone", value: BRAND.phone, href: phoneHref },
  { key: "email", icon: Mail, title: "Email", value: BRAND.email, href: `mailto:${BRAND.email}` },
  { key: "whatsapp", icon: MessageCircle, title: "WhatsApp", value: BRAND.whatsapp, href: whatsappHref },
  { key: "hours", icon: Clock, title: "Business Hours", value: BRAND.hours },
];

export default function Contact() {
  return (
    <>
      <PageHero
        chapter="Contact Us"
        title="Speak with our wholesale team"
        lead="Call, email or message us about products, delivery, wholesale pricing or your BlueFig ordering account."
      />

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <Reveal className="grid md:grid-cols-2 gap-5" data-testid="contact-information">
            {CONTACTS.map(({ key, icon, title, value, href }, index) => (
              <ContactCard key={key} icon={icon} title={title} value={value} href={href} testId={`contact-${key}`} wide={index === 0} />
            ))}
          </Reveal>

          <Reveal delay={0.1} className="mt-10 rounded-3xl border border-forest/15 bg-forest text-cream p-8 md:p-10 shadow-hard-lg">
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <p className="overline text-butter-deep">Wholesale Account</p>
                <h2 className="font-display text-3xl md:text-4xl text-butter tracking-tight mt-3">
                  Customer accounts and ordering live in BlueFig.
                </h2>
                <p className="text-cream/75 mt-4 max-w-2xl">
                  This website is fully static. Your login, customer profile, pricing, invoices and orders are managed securely inside the BlueFig portal.
                </p>
              </div>
              <PortalButton variant="butter" testId="contact-bluefig-btn">Login / Sign Up on BlueFig</PortalButton>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="mt-10 rounded-2xl overflow-hidden border border-forest/15 h-64 relative bg-forest">
            <div className="absolute inset-0 opacity-[0.12] noise-bg" />
            <a href={mapsHref} target="_blank" rel="noreferrer" data-testid="contact-map-link" className="absolute inset-0 grid place-items-center text-center px-6 hover:bg-white/5 transition-colors">
              <div>
                <MapPin className="h-8 w-8 text-butter-deep mx-auto" />
                <p className="text-cream font-medium mt-2">Open in Google Maps</p>
                <p className="text-cream/70 text-sm mt-1 font-mono-accent">{BRAND.address}</p>
              </div>
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function ContactCard({ icon: Icon, title, value, href, testId, wide }) {
  const className = `flex gap-4 rounded-2xl border border-forest/15 bg-white p-5 hover:border-forest/35 transition-colors ${wide ? "md:col-span-2" : ""}`;
  const content = (
    <>
      <span className="grid place-items-center h-11 w-11 rounded-xl bg-sage text-forest shrink-0"><Icon className="h-5 w-5" /></span>
      <span>
        <span className="overline text-forest/50 block">{title}</span>
        <span className="text-sm text-forest mt-1 block">{value}</span>
      </span>
    </>
  );

  if (!href) {
    return <div data-testid={testId} className={className}>{content}</div>;
  }

  const external = href.startsWith("http");
  return (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} data-testid={testId} className={className}>
      {content}
    </a>
  );
}
